// pages/Officer/StrategyDocsPage.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Badge,
  Grid,
  Collapse,
  Alert,
  Tooltip,
  Button,
  TextField,
  Stack,
  CircularProgress,
  ListItemButton,
  useTheme,
  alpha,
  styled,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stepper,
  Step,
  StepLabel,
  StepContent,
} from '@mui/material';
import {
  TrendingUp,
  CheckCircle,
  Warning,
  Circle,
  ExpandMore,
  ExpandLess,
  Book,
  Analytics,
  History,
  Edit,
  Save,
  Cancel,
  ContentCopy,
  Download,
  Refresh,
  Code,
  Search,
  Settings,
  KeyboardArrowRight,
  FolderOpen,
  Info,
  PlayArrow,
  Pause,
  TestsTaken,
} from '@mui/icons-material';

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  backgroundColor: '#151932',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: theme.spacing(1),
  '& .MuiCardContent-root': {
    padding: theme.spacing(3),
  },
}));

const GroupedStrategyItem = styled(ListItemButton)(({ theme, level }) => ({
  marginBottom: theme.spacing(0.5),
  borderRadius: theme.spacing(1),
  border: '1px solid rgba(102, 126, 234, 0.2)',
  paddingLeft: theme.spacing(2 + (level || 0) * 2),
  backgroundColor: level ? 'rgba(102, 126, 234, 0.05)' : 'rgba(102, 126, 234, 0.08)',
  '&:hover': {
    backgroundColor: 'rgba(102, 126, 234, 0.15)',
    borderColor: 'rgba(102, 126, 234, 0.4)',
  },
  transition: 'all 0.3s ease',
}));

const CodeBlock = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(0, 0, 0, 0.4)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  borderRadius: theme.spacing(1),
  padding: theme.spacing(2),
  fontFamily: 'monospace',
  fontSize: '0.85rem',
  color: '#00ff88',
  overflow: 'auto',
  maxHeight: 400,
  lineHeight: 1.6,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
}));

const ConditionBadge = styled(Box)(({ type }) => {
  const colors = {
    simultaneous: { bg: 'rgba(76, 175, 80, 0.1)', border: 'rgba(76, 175, 80, 0.3)', text: '#4CAF50' },
    timed: { bg: 'rgba(102, 126, 234, 0.1)', border: 'rgba(102, 126, 234, 0.3)', text: '#667eea' },
    sequential_exclusion: { bg: 'rgba(255, 152, 0, 0.1)', border: 'rgba(255, 152, 0, 0.3)', text: '#FFA500' },
    three_stage: { bg: 'rgba(156, 39, 176, 0.1)', border: 'rgba(156, 39, 176, 0.3)', text: '#9C27B0' },
  };

  const color = colors[type] || colors.timed;

  return {
    padding: '8px 12px',
    backgroundColor: color.bg,
    border: `1px solid ${color.border}`,
    borderRadius: '6px',
    color: color.text,
    fontSize: '0.85rem',
    fontWeight: 600,
  };
});

const StrategyDocsPage = ({ apiBaseUrl = window.location.origin }) => {
  const theme = useTheme();
  const [strategies, setStrategies] = useState([]);
  const [selectedStrategy, setSelectedStrategy] = useState(null);
  const [strategyDetail, setStrategyDetail] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedGroups, setExpandedGroups] = useState({});
  const [testStep, setTestStep] = useState(0);
  const [testRunning, setTestRunning] = useState(false);
  const [testResults, setTestResults] = useState([]);
  const [codeType, setCodeType] = useState('python');
  const ws = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // WebSocket connection
  useEffect(() => {
    connectWebSocket();
    return () => {
      if (ws.current) {
        ws.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, []);

  const connectWebSocket = () => {
    try {
      const wsUrl = `ws://${apiBaseUrl
        .replace('http://', '')
        .replace('https://', '')}/ws/strategy-alerts/`;
      ws.current = new WebSocket(wsUrl);
      ws.current.onopen = () => setWsConnected(true);
      ws.current.onclose = () => {
        setWsConnected(false);
        reconnectTimeoutRef.current = setTimeout(connectWebSocket, 5000);
      };
    } catch (error) {
      reconnectTimeoutRef.current = setTimeout(connectWebSocket, 5000);
    }
  };

  // Fetch strategies
  const fetchStrategies = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${apiBaseUrl}/strategies/api/strategy/list/`);
      const data = await response.json();
      if (data.success) {
        setStrategies(data.strategies);
        if (!selectedStrategy && data.strategies.length > 0) {
          setSelectedStrategy(data.strategies[0].strategy_id);
        }
      }
    } catch (error) {
      console.error('Error fetching strategies:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch strategy detail with configuration
  const fetchStrategyDetail = async (strategyId) => {
    setLoading(true);
    try {
      const response = await fetch(
        `${apiBaseUrl}/strategies/api/strategy/detail/${strategyId}/`
      );
      const data = await response.json();
      if (data.success) {
        setStrategyDetail(data);
        setTestResults([]);
        setTestStep(0);
      }
    } catch (error) {
      console.error('Error fetching strategy detail:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStrategies();
  }, []);

  const handleStrategySelect = (strategyId) => {
    setSelectedStrategy(strategyId);
    fetchStrategyDetail(strategyId);
    setActiveTab(0);
  };

  // Test sequence execution
  const runTestSequence = async () => {
    if (!strategyDetail || testRunning) return;

    const testSeq = strategyDetail.configuration?.test_sequence || [];
    
    setTestRunning(true);
    setTestResults([]);
    setTestStep(0);

    for (let i = 0; i < testSeq.length; i++) {
      const testStep = testSeq[i];
      
      // Wait before sending signal
      if (testStep.delay_minutes > 0) {
        await new Promise(resolve => setTimeout(resolve, testStep.delay_minutes * 1000));
      }

      try {
        const payload = {
          strategy_id: selectedStrategy,
          signal_type: testStep.signal,
          data: {
            price: testStep.price,
            timestamp: new Date().toISOString(),
            indicator: testStep.signal,
            timeframe: strategyDetail.strategy.timeframe
          }
        };

        const response = await fetch(`${apiBaseUrl}/strategies/api/signal/alert/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        const result = await response.json();

        setTestResults(prev => [...prev, {
          step: i + 1,
          name: testStep.name || testStep.signal,
          signal: testStep.signal,
          price: testStep.price,
          status: result.signal_type,
          message: result.message,
          timestamp: new Date().toLocaleTimeString()
        }]);

        setTestStep(i + 1);
      } catch (error) {
        setTestResults(prev => [...prev, {
          step: i + 1,
          name: testStep.name || testStep.signal,
          signal: testStep.signal,
          price: testStep.price,
          status: 'error',
          message: error.message,
          timestamp: new Date().toLocaleTimeString()
        }]);
      }
    }

    setTestRunning(false);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  // Filter strategies based on search
  const filteredStrategies = strategies.filter((strategy) =>
    strategy.strategy_id?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getTradingViewCode = (type) => {
    const webhookUrl = `${apiBaseUrl}/strategies/api/signal/alert/`;
    const strategyId = selectedStrategy || 'STRATEGY_ID';
    const testSeq = strategyDetail?.configuration?.test_sequence || [];

    const codes = {
      python: `import requests
import json
import time
from datetime import datetime

class StrategyTester:
    def __init__(self, webhook_url, strategy_id):
        self.webhook_url = webhook_url
        self.strategy_id = strategy_id
    
    def send_signal(self, signal_type, price, indicator=""):
        payload = {
            "strategy_id": self.strategy_id,
            "signal_type": signal_type,
            "data": {
                "price": price,
                "timestamp": datetime.now().isoformat(),
                "indicator": indicator,
                "timeframe": "5m"
            }
        }
        
        response = requests.post(self.webhook_url, json=payload)
        print(f"[{datetime.now().strftime('%H:%M:%S')}] {signal_type} @ {price} → {response.json().get('signal_type')}")
        return response.json()
    
    def run_test_sequence(self):
        print(f"\\n🚀 Testing {self.strategy_id}\\n")
        test_sequence = ${JSON.stringify(testSeq, null, 2)}
        
        for idx, step in enumerate(test_sequence, 1):
            print(f"Step {idx}: {step['name']}")
            
            if step.get('delay_minutes', 0) > 0:
                print(f"  ⏳ Waiting {step['delay_minutes']} minutes...")
                time.sleep(step['delay_minutes'] * 60)
            
            self.send_signal(
                step['signal'],
                step['price'],
                step.get('signal', '')
            )
            print()

tester = StrategyTester("${webhookUrl}", "${strategyId}")
tester.run_test_sequence()
`,

      javascript: `// Complete test sequence
const strategyTester = {
  webhookUrl: "${webhookUrl}",
  strategyId: "${strategyId}",
  testSequence: ${JSON.stringify(testSeq, null, 2)},
  
  async sendSignal(signalType, price, indicator = "") {
    const payload = {
      strategy_id: this.strategyId,
      signal_type: signalType,
      data: {
        price: price,
        timestamp: new Date().toISOString(),
        indicator: indicator,
        timeframe: "5m"
      }
    };
    
    try {
      const response = await fetch(this.webhookUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      const result = await response.json();
      console.log(\`[\${new Date().toLocaleTimeString()}] \${signalType} @ \${price}\`, result);
      return result;
    } catch (error) {
      console.error('Error:', error);
    }
  },
  
  async runTestSequence() {
    console.log(\`🚀 Testing \${this.strategyId}\\n\`);
    
    for (let i = 0; i < this.testSequence.length; i++) {
      const step = this.testSequence[i];
      console.log(\`Step \${i + 1}: \${step.name}\`);
      
      if (step.delay_minutes > 0) {
        console.log(\`⏳ Waiting \${step.delay_minutes} minutes...\`);
        await new Promise(r => setTimeout(r, step.delay_minutes * 60 * 1000));
      }
      
      await this.sendSignal(step.signal, step.price, step.signal);
      console.log();
    }
  }
};

// Run it
strategyTester.runTestSequence();
`,

      curl: `#!/bin/bash
# Test sequence for ${strategyId}

WEBHOOK_URL="${webhookUrl}"
STRATEGY_ID="${strategyId}"

echo "🚀 Testing \$STRATEGY_ID"
echo ""

${testSeq.map((step, idx) => `
# Step ${idx + 1}: ${step.name}
echo "Step ${idx + 1}: ${step.name}"
${step.delay_minutes > 0 ? `sleep ${Math.floor(step.delay_minutes * 60)}` : ''}
curl -X POST $WEBHOOK_URL \\
  -H "Content-Type: application/json" \\
  -d '{
    "strategy_id": "'$STRATEGY_ID'",
    "signal_type": "${step.signal}",
    "data": {
      "price": ${step.price},
      "timestamp": "'$(date -u +%Y-%m-%dT%H:%M:%SZ)'",
      "indicator": "${step.signal}",
      "timeframe": "5m"
    }
  }' | jq .
echo ""
`).join('\n')}
`,
    };

    return codes[type] || codes.python;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 2, p: 2 }}>
      {/* Header with Search */}
      <StyledCard>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                placeholder="Search strategies..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: '#667eea' }} />,
                }}
                sx={{
                  '& .MuiInputBase-root': {
                    color: '#fff',
                    bgcolor: 'rgba(255,255,255,0.05)',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
                <Badge
                  badgeContent={wsConnected ? 'LIVE' : 'OFF'}
                  color={wsConnected ? 'success' : 'error'}
                >
                  <Circle sx={{ fontSize: 14, color: wsConnected ? '#4CAF50' : '#f44336' }} />
                </Badge>
                <Button
                  size="small"
                  startIcon={<Refresh />}
                  onClick={fetchStrategies}
                  sx={{ color: '#667eea' }}
                >
                  Refresh
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </StyledCard>

      {/* Main Content */}
      <Box sx={{ display: 'flex', gap: 2, flex: 1, minHeight: 0 }}>
        {/* Strategy List */}
        <Paper
          sx={{
            width: { xs: '100%', sm: 300 },
            bgcolor: '#151932',
            borderRadius: 1,
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid rgba(255,255,255,0.1)',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ p: 2, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
            <Typography variant="subtitle2" sx={{ color: '#667eea', fontWeight: 600 }}>
              {filteredStrategies.length} Strategies
            </Typography>
          </Box>

          <List sx={{ flex: 1, overflow: 'auto', p: 1 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={30} sx={{ color: '#667eea' }} />
              </Box>
            ) : filteredStrategies.length > 0 ? (
              filteredStrategies.map((strategy) => (
                <GroupedStrategyItem
                  key={strategy.strategy_id}
                  selected={selectedStrategy === strategy.strategy_id}
                  onClick={() => handleStrategySelect(strategy.strategy_id)}
                  sx={{
                    background: selectedStrategy === strategy.strategy_id
                      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 0.3)'
                      : 'transparent',
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Badge
                      variant="dot"
                      color={strategy.is_active ? 'success' : 'default'}
                    >
                      <TrendingUp sx={{ fontSize: 18, color: '#667eea' }} />
                    </Badge>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600 }}>
                        {strategy.strategy_id}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                        {strategy.symbol} • {strategy.timeframe}
                      </Typography>
                    }
                  />
                </GroupedStrategyItem>
              ))
            ) : (
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', p: 2 }}>
                No strategies found
              </Typography>
            )}
          </List>
        </Paper>

        {/* Details Panel */}
        {selectedStrategy && strategyDetail ? (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
            {/* Tabs */}
            <Box sx={{ display: 'flex', gap: 1, overflow: 'auto', pb: 1 }}>
              {['Overview', 'Configuration', 'Test Sequence', 'Developer Docs', 'TradingView'].map((label, idx) => (
                <Button
                  key={idx}
                  variant={activeTab === idx ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setActiveTab(idx)}
                  sx={{
                    background:
                      activeTab === idx
                        ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                        : 'transparent',
                    border: activeTab === idx ? 'none' : '1px solid rgba(255,255,255,0.2)',
                    color: activeTab === idx ? '#fff' : '#667eea',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {label}
                </Button>
              ))}
            </Box>

            {/* Content Area */}
            <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Overview Tab */}
              {activeTab === 0 && (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <StyledCard>
                      <CardContent>
                        <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
                          {strategyDetail.strategy.id}
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={6} sm={3}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                                Symbol
                              </Typography>
                              <Typography variant="body2" sx={{ color: '#fff', mt: 1 }}>
                                {strategyDetail.strategy.symbol}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                                Timeframe
                              </Typography>
                              <Typography variant="body2" sx={{ color: '#fff', mt: 1 }}>
                                {strategyDetail.strategy.timeframe}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                                Status
                              </Typography>
                              <Chip
                                label={strategyDetail.strategy.is_active ? 'Active' : 'Paused'}
                                color={strategyDetail.strategy.is_active ? 'success' : 'default'}
                                size="small"
                                sx={{ mt: 1 }}
                              />
                            </Box>
                          </Grid>
                          <Grid item xs={6} sm={3}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                                Entries
                              </Typography>
                              <Typography variant="h6" sx={{ color: '#4CAF50', mt: 1 }}>
                                {strategyDetail.metrics?.total_entries || 0}
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </StyledCard>
                  </Grid>
                </Grid>
              )}

              {/* Configuration Tab */}
              {activeTab === 1 && (
                <StyledCard>
                  <CardContent>
                    <Typography variant="h6" sx={{ color: '#fff', mb: 3 }}>
                      Strategy Configuration
                    </Typography>
                    <Stack spacing={2}>
                      {strategyDetail.configuration?.conditions?.map((condition, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            p: 2,
                            bgcolor: 'rgba(255,255,255,0.05)',
                            borderRadius: 1,
                            border: '1px solid rgba(255,255,255,0.1)',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ color: '#fff', flex: 1 }}>
                              Condition {condition.id || idx + 1}: {condition.name}
                            </Typography>
                            <Box sx={ConditionBadge({ type: condition.type })}>
                              {condition.type}
                            </Box>
                          </Box>

                          {condition.type === 'simultaneous' && (
                            <Stack spacing={1}>
                              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                                Signal 1: {typeof condition.signal1 === 'object' ? condition.signal1.name : condition.signal1}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                                Signal 2: {typeof condition.signal2 === 'object' ? condition.signal2.name : condition.signal2}
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#FFA500' }}>
                                Tolerance: {condition.tolerance_seconds}s
                              </Typography>
                            </Stack>
                          )}

                          {condition.type === 'timed' && (
                            <Stack spacing={1}>
                              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                                Trigger: {typeof condition.trigger_signal === 'object' ? condition.trigger_signal.name : condition.trigger_signal}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                                Target: {typeof condition.target_signal === 'object' ? condition.target_signal.name : condition.target_signal}
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#667eea' }}>
                                Time Window: {condition.min_minutes}m - {condition.max_minutes}m
                              </Typography>
                            </Stack>
                          )}

                          {condition.type === 'sequential_exclusion' && (
                            <Stack spacing={1}>
                              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                                Early: {typeof condition.early_signal === 'object' ? condition.early_signal.name : condition.early_signal}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                                Confirmation: {typeof condition.confirmation_signal === 'object' ? condition.confirmation_signal.name : condition.confirmation_signal}
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#FF5722' }}>
                                Exclude: {typeof condition.excluded_signal === 'object' ? condition.excluded_signal.name : condition.excluded_signal}
                              </Typography>
                            </Stack>
                          )}

                          {condition.type === 'three_stage' && (
                            <Stack spacing={1}>
                              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                                Stage 1: {typeof condition.signal_a === 'object' ? condition.signal_a.name : condition.signal_a}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                                Stage 2: {typeof condition.signal_b === 'object' ? condition.signal_b.name : condition.signal_b}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                                Stage 3: {typeof condition.signal_c === 'object' ? condition.signal_c.name : condition.signal_c}
                              </Typography>
                            </Stack>
                          )}
                        </Box>
                      ))}

                      <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 1 }} />

                      <Box
                        sx={{
                          p: 2,
                          bgcolor: 'rgba(244, 67, 54, 0.1)',
                          border: '1px solid rgba(244, 67, 54, 0.3)',
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ color: '#f44336', mb: 1 }}>
                          Exit Signal
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#fff' }}>
                          {strategyDetail.configuration?.exit_signal}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </StyledCard>
              )}

              {/* Test Sequence Tab */}
              {activeTab === 2 && (
                <StyledCard>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                      <Typography variant="h6" sx={{ color: '#fff' }}>
                        Test Sequence Runner
                      </Typography>
                      <Button
                        startIcon={testRunning ? <Pause /> : <PlayArrow />}
                        onClick={runTestSequence}
                        disabled={testRunning}
                        sx={{
                          background: testRunning ? 'rgba(255,255,255,0.1)' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          color: '#fff',
                        }}
                      >
                        {testRunning ? 'Running...' : 'Run Test'}
                      </Button>
                    </Box>

                    {/* Test Steps */}
                    <Stepper activeStep={testStep} orientation="vertical">
                      {strategyDetail.configuration?.test_sequence?.map((step, idx) => (
                        <Step key={idx}>
                          <StepLabel
                            sx={{
                              '& .MuiStepLabel-label': {
                                color: '#fff !important',
                              },
                            }}
                          >
                            {step.name || step.signal}
                          </StepLabel>
                          <StepContent>
                            <Stack spacing={1} sx={{ mb: 2 }}>
                              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                                Signal: <span style={{ color: '#667eea', fontWeight: 600 }}>{step.signal}</span>
                              </Typography>
                              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                                Price: <span style={{ color: '#4CAF50', fontWeight: 600 }}>${step.price}</span>
                              </Typography>
                              {step.delay_minutes > 0 && (
                                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                                  Delay: <span style={{ color: '#FFA500', fontWeight: 600 }}>{step.delay_minutes}m</span>
                                </Typography>
                              )}
                            </Stack>

                            {testResults[idx] && (
                              <Alert
                                severity={testResults[idx].status === 'error' ? 'error' : 'success'}
                                sx={{ mb: 1 }}
                              >
                                <strong>{testResults[idx].message}</strong>
                                <br />
                                <small>Status: {testResults[idx].status}</small>
                              </Alert>
                            )}
                          </StepContent>
                        </Step>
                      ))}
                    </Stepper>
                  </CardContent>
                </StyledCard>
              )}

              {/* Developer Docs Tab */}
              {activeTab === 3 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <StyledCard>
                    <CardContent>
                      <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
                        Strategy Configuration JSON
                      </Typography>
                      <CodeBlock>
{JSON.stringify(strategyDetail.configuration, null, 2)}
                      </CodeBlock>
                      <Button
                        fullWidth
                        startIcon={<ContentCopy />}
                        onClick={() => copyToClipboard(JSON.stringify(strategyDetail.configuration, null, 2))}
                        sx={{
                          mt: 2,
                          color: '#667eea',
                          borderColor: '#667eea',
                        }}
                        variant="outlined"
                        size="small"
                      >
                        Copy Configuration
                      </Button>
                    </CardContent>
                  </StyledCard>

                  <StyledCard>
                    <CardContent>
                      <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
                        API Integration
                      </Typography>
                      <Stack spacing={2}>
                        <Box>
                          <Typography variant="subtitle2" sx={{ color: '#667eea', mb: 1 }}>
                            Setup Endpoint
                          </Typography>
                          <CodeBlock sx={{ fontSize: '0.75rem' }}>
{`POST /strategies/api/strategy/setup/

{
  "strategy_id": "${strategyDetail.strategy.id}",
  "symbol": "${strategyDetail.strategy.symbol}",
  "timeframe": "${strategyDetail.strategy.timeframe}",
  ...configuration
}`}
                          </CodeBlock>
                        </Box>

                        <Box>
                          <Typography variant="subtitle2" sx={{ color: '#4CAF50', mb: 1 }}>
                            Signal Endpoint
                          </Typography>
                          <CodeBlock sx={{ fontSize: '0.75rem' }}>
{`POST /strategies/api/signal/alert/

{
  "strategy_id": "${strategyDetail.strategy.id}",
  "signal": "SIGNAL_NAME",
  "timeframe": "${strategyDetail.strategy.timeframe}",
  "price": 0.0,
  "timestamp": "2025-10-16T10:30:00Z"
}`}
                          </CodeBlock>
                        </Box>
                      </Stack>
                    </CardContent>
                  </StyledCard>
                </Box>
              )}

              {/* TradingView Tab */}
              {activeTab === 4 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <StyledCard>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="h6" sx={{ color: '#fff' }}>
                          Code Examples
                        </Typography>
                        <FormControl size="small" sx={{ width: 120 }}>
                          <Select
                            value={codeType}
                            onChange={(e) => setCodeType(e.target.value)}
                            sx={{
                              color: '#fff',
                              bgcolor: 'rgba(255,255,255,0.05)',
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'rgba(102, 126, 234, 0.3)',
                              },
                            }}
                          >
                            <MenuItem value="python">Python</MenuItem>
                            <MenuItem value="javascript">JavaScript</MenuItem>
                            <MenuItem value="curl">cURL</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>

                      <CodeBlock>
{getTradingViewCode(codeType)}
                      </CodeBlock>

                      <Button
                        fullWidth
                        startIcon={<ContentCopy />}
                        onClick={() => copyToClipboard(getTradingViewCode(codeType))}
                        sx={{
                          mt: 2,
                          color: '#667eea',
                          borderColor: '#667eea',
                        }}
                        variant="outlined"
                      >
                        Copy Code
                      </Button>
                    </CardContent>
                  </StyledCard>
                </Box>
              )}
            </Box>
          </Box>
        ) : (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.5)' }}>
              Select a strategy to view details
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default StrategyDocsPage;
